/**
 * AdminStylePresetsTab — Grid editor for the 50 seeded style presets.
 *
 * Replaces the previous "curl + admin endpoint" workflow with an inline
 * table the admin can use to (1) toggle a preset's `enabled` flag,
 * (2) paste a 12-second preview MP3 URL, and (3) bulk-save changes.
 *
 * Data flow:
 *   GET  /api/admin/style-presets         → load full list (incl. payloads)
 *   PATCH /api/admin/style-presets/:id    → save edits per row
 *   POST /api/admin/style-presets/reseed  → reseed the library
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Save, Search, Music, ExternalLink, CheckCircle, XCircle, Upload, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

export default function AdminStylePresetsTab() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());
  const [reseedingAll, setReseedingAll] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  // Edit buffer keyed by preset_id; only dirty rows get sent on save.
  const [edits, setEdits] = useState({});
  // Upload state: { uploading: Set<id>, progress: {id: 0..100} } and
  // a queue of recent results so the user can see what just happened.
  const [uploadingIds, setUploadingIds] = useState(new Set());
  const [trimmingIds, setTrimmingIds] = useState(new Set());
  const [uploadLog, setUploadLog] = useState([]); // [{id, status, name, error?}]
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/style-presets`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setPresets(Array.isArray(data) ? data : []);
      setEdits({});
    } catch (e) {
      toast.error(`Failed to load presets: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const set = new Set(presets.map((p) => p.category).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [presets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return presets.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (!q) return true;
      return (
        (p.artist_name || '').toLowerCase().includes(q) ||
        (p.style_tags || '').toLowerCase().includes(q) ||
        (p.preset_id || '').toLowerCase().includes(q)
      );
    });
  }, [presets, query, category]);

  const updateField = (id, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const valueFor = (preset, field) => {
    const edit = edits[preset.preset_id];
    if (edit && Object.prototype.hasOwnProperty.call(edit, field)) return edit[field];
    return preset[field];
  };

  const isDirty = (id) => Boolean(edits[id] && Object.keys(edits[id]).length > 0);

  const saveRow = async (preset) => {
    const id = preset.preset_id;
    const patch = edits[id];
    if (!patch) return;
    setSavingIds((s) => new Set(s).add(id));
    try {
      const r = await fetch(`${API}/admin/style-presets/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Merge into cached row and clear the dirty buffer
      setPresets((ps) => ps.map((p) => (p.preset_id === id ? { ...p, ...patch } : p)));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success(`Saved ${preset.artist_name}`);
    } catch (e) {
      toast.error(`Failed to save ${preset.artist_name}: ${e.message}`);
    } finally {
      setSavingIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const saveAllDirty = async () => {
    const ids = Object.keys(edits);
    if (ids.length === 0) {
      toast.info('No changes to save');
      return;
    }
    for (const id of ids) {
      const preset = presets.find((p) => p.preset_id === id);
      if (preset) await saveRow(preset);
    }
  };

  const reseed = async () => {
    if (!window.confirm('Reseed the style preset library? This is idempotent — existing rows stay, new ones from the seed list get inserted.')) return;
    setReseedingAll(true);
    try {
      const r = await fetch(`${API}/admin/style-presets/reseed`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      toast.success(`Reseed complete: +${data.inserted || 0} inserted, ${data.updated || 0} updated, ${data.skipped || 0} skipped`);
      await load();
    } catch (e) {
      toast.error(`Reseed failed: ${e.message}`);
    } finally {
      setReseedingAll(false);
    }
  };

  const dirtyCount = Object.keys(edits).length;
  const enabledCount = presets.filter((p) => valueFor(p, 'enabled') !== false).length;
  const withPreviewCount = presets.filter((p) => (valueFor(p, 'preview_audio_url') || '').trim()).length;

  // -------- Upload Helper ---------------------------------------------------
  // Build a quick lookup of preset_id → exists, so we can match dropped
  // filenames against the seeded library (filename stem === preset_id).
  const presetIdSet = useMemo(() => new Set(presets.map((p) => p.preset_id)), [presets]);

  /** Strip extension + normalize to lowercase-kebab so "ABBA.mp3", "abba.MP3",
   *  "Abba (1).mp3" all map to preset_id "abba". Last numeric "(2)" suffix
   *  is trimmed too so admins can re-upload without renaming. */
  const filenameToPresetId = useCallback((filename) => {
    const stem = (filename || '').replace(/\.[a-z0-9]+$/i, '');
    return stem
      .toLowerCase()
      .replace(/\s*\(\d+\)\s*$/, '')
      .trim()
      .replace(/\s+/g, '-');
  }, []);

  const uploadOnePreview = useCallback(async (presetId, file) => {
    setUploadingIds((s) => new Set(s).add(presetId));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API}/admin/style-presets/${presetId}/preview-audio`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      // Server now stores the FULL track URL. Preview is created later via /trim.
      setPresets((ps) =>
        ps.map((p) =>
          p.preset_id === presetId
            ? { ...p, full_audio_url: data.full_audio_url, full_audio_duration_sec: data.duration_sec }
            : p,
        ),
      );
      setUploadLog((log) => [
        { id: presetId, name: file.name, status: 'ok', size_kb: data.size_kb, action: 'uploaded' },
        ...log,
      ].slice(0, 20));
      return { ok: true };
    } catch (e) {
      setUploadLog((log) => [
        { id: presetId, name: file.name, status: 'error', error: e.message },
        ...log,
      ].slice(0, 20));
      toast.error(`${presetId}: ${e.message}`);
      return { ok: false, error: e.message };
    } finally {
      setUploadingIds((s) => {
        const next = new Set(s);
        next.delete(presetId);
        return next;
      });
    }
  }, []);

  /** Trim a 12s preview from the preset's stored full track, starting at
   *  the admin-chosen `start_sec`. Useful for landing on the chorus rather
   *  than the verse. Updates `preview_audio_url` server-side. */
  const trimPreview = useCallback(async (presetId, startSec, durationSec = 12) => {
    setTrimmingIds((s) => new Set(s).add(presetId));
    try {
      const r = await fetch(`${API}/admin/style-presets/${presetId}/trim-preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_sec: Number(startSec), duration_sec: Number(durationSec) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setPresets((ps) =>
        ps.map((p) =>
          p.preset_id === presetId
            ? {
                ...p,
                preview_audio_url: data.preview_audio_url,
                preview_start_sec: data.start_sec,
                preview_duration_sec: data.duration_sec,
              }
            : p,
        ),
      );
      setUploadLog((log) => [
        { id: presetId, name: `@${startSec}s`, status: 'ok', size_kb: data.size_kb, action: 'trimmed' },
        ...log,
      ].slice(0, 20));
      toast.success(`Trimmed ${presetId} @ ${startSec}s`);
    } catch (e) {
      toast.error(`Trim ${presetId}: ${e.message}`);
    } finally {
      setTrimmingIds((s) => {
        const next = new Set(s);
        next.delete(presetId);
        return next;
      });
    }
  }, []);

  /** Handle one or more dropped/selected files. Files whose stem matches a
   *  preset_id are uploaded; unmatched files are logged for the admin. */
  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const matched = [];
    const unmatched = [];
    for (const f of list) {
      const pid = filenameToPresetId(f.name);
      if (presetIdSet.has(pid)) {
        matched.push([pid, f]);
      } else {
        unmatched.push(f.name);
      }
    }
    if (unmatched.length) {
      toast.warning(
        `Skipping ${unmatched.length} file(s) — filename must match a preset_id: ${unmatched.slice(0, 3).join(', ')}${unmatched.length > 3 ? '…' : ''}`,
      );
    }
    if (matched.length === 0) return;
    toast.info(`Uploading ${matched.length} preview${matched.length === 1 ? '' : 's'}…`);
    // Upload in parallel — they're small (<500KB each).
    const results = await Promise.all(matched.map(([pid, f]) => uploadOnePreview(pid, f)));
    const okCount = results.filter((r) => r.ok).length;
    if (okCount > 0) {
      toast.success(`${okCount} preview${okCount === 1 ? '' : 's'} uploaded`);
    }
  }, [presetIdSet, filenameToPresetId, uploadOnePreview]);

  // Drag-and-drop wiring on the drop zone div.
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);
  // -------------------------------------------------------------------------

  return (
    <div className="glass-card rounded-xl p-6" data-testid="admin-style-presets-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Music className="h-5 w-5 text-purple-400" />
            Style Presets
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            {presets.length} total • {enabledCount} enabled • {withPreviewCount} with preview audio
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reseed}
            disabled={reseedingAll}
            className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            data-testid="admin-presets-reseed-btn"
          >
            {reseedingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Reseed
          </button>
          <button
            onClick={saveAllDirty}
            disabled={dirtyCount === 0}
            className="px-3 py-2 bg-purple-500 hover:bg-purple-400 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm rounded-lg flex items-center gap-2 transition"
            data-testid="admin-presets-save-all-btn"
          >
            <Save className="h-4 w-4" />
            Save {dirtyCount > 0 ? `(${dirtyCount})` : 'All'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-black/30 rounded-lg px-3 py-2 border border-white/10">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by artist, tag, or preset_id…"
            className="flex-1 bg-transparent text-white text-sm placeholder:text-zinc-500 outline-none"
            data-testid="admin-presets-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-black/30 text-white text-sm px-3 py-2 rounded-lg border border-white/10 outline-none"
          data-testid="admin-presets-category-filter"
        >
          {categories.map((c) => (
            <option key={c} value={c} className="bg-zinc-800">{c === 'all' ? 'All categories' : c}</option>
          ))}
        </select>
      </div>

      {/* Drop zone — drag MP3s here; filename stem must match preset_id (e.g. abba.mp3) */}
      <div
        ref={dropZoneRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`mb-4 rounded-lg border-2 border-dashed p-4 cursor-pointer transition ${
          dragActive
            ? 'border-purple-400 bg-purple-500/10'
            : 'border-white/20 hover:border-white/30 bg-black/20'
        }`}
        data-testid="admin-presets-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = ''; // allow re-selecting the same file
          }}
          data-testid="admin-presets-file-input"
        />
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${dragActive ? 'bg-purple-500/30' : 'bg-white/5'}`}>
            <Upload className={`h-5 w-5 ${dragActive ? 'text-purple-300' : 'text-white/70'}`} />
          </div>
          <div className="flex-1">
            <div className="text-sm text-white font-medium">
              {dragActive ? 'Drop full MusicGPT tracks here…' : 'Drop full MusicGPT tracks (or click to browse)'}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Filename stem must match a preset_id (e.g. <code className="text-purple-300">abba.mp3</code> →
              {' '}preset <code className="text-purple-300">abba</code>). After upload, set the start time and click <span className="text-purple-300">Trim</span> to lock in the 12s chorus preview.
            </div>
          </div>
          {uploadingIds.size > 0 && (
            <div className="text-xs text-purple-300 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {uploadingIds.size} uploading…
            </div>
          )}
        </div>
      </div>

      {/* Recent uploads strip — last 5 results */}
      {uploadLog.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-presets-upload-log">
          {uploadLog.slice(0, 5).map((entry, i) => (
            <div
              key={`${entry.id}-${i}`}
              className={`text-xs px-2 py-1 rounded flex items-center gap-1.5 ${
                entry.status === 'ok' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'
              }`}
            >
              {entry.status === 'ok' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              <span className="font-medium">{entry.id}</span>
              <span className="opacity-70">
                {entry.status === 'ok' ? `${entry.size_kb} KB` : entry.error}
              </span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading presets…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No presets match your filters.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-zinc-400">
                <th className="py-2 px-2 w-12">On</th>
                <th className="py-2 px-2">Artist</th>
                <th className="py-2 px-2">Category</th>
                <th className="py-2 px-2 w-20">Order</th>
                <th className="py-2 px-2">Style tags</th>
                <th className="py-2 px-2">Full track</th>
                <th className="py-2 px-2">Preview (12s)</th>
                <th className="py-2 px-2 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const id = p.preset_id;
                const dirty = isDirty(id);
                const saving = savingIds.has(id);
                const enabled = valueFor(p, 'enabled') !== false;
                const previewUrl = valueFor(p, 'preview_audio_url') || '';
                const fullUrl = valueFor(p, 'full_audio_url') || '';
                const fullDur = p.full_audio_duration_sec;
                const startSec = valueFor(p, 'preview_start_sec');
                const startDisplay = startSec === null || startSec === undefined ? '' : String(startSec);
                const trimming = trimmingIds.has(id);
                return (
                  <tr
                    key={id}
                    className={`border-b border-white/5 ${dirty ? 'bg-purple-500/5' : 'hover:bg-white/5'}`}
                    data-testid={`admin-preset-row-${id}`}
                  >
                    <td className="py-2 px-2">
                      <button
                        onClick={() => updateField(id, 'enabled', !enabled)}
                        className={enabled ? 'text-emerald-400' : 'text-zinc-500'}
                        title={enabled ? 'Click to disable' : 'Click to enable'}
                        data-testid={`admin-preset-toggle-${id}`}
                      >
                        {enabled ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-white">
                      <div className="font-medium">{p.artist_name}</div>
                      <div className="text-zinc-500 text-xs">{id}</div>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        value={valueFor(p, 'category') || ''}
                        onChange={(e) => updateField(id, 'category', e.target.value)}
                        className="w-32 bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        value={valueFor(p, 'display_order') ?? 0}
                        onChange={(e) => updateField(id, 'display_order', Number(e.target.value) || 0)}
                        className="w-16 bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        value={valueFor(p, 'style_tags') || ''}
                        onChange={(e) => updateField(id, 'style_tags', e.target.value)}
                        className="w-full min-w-[200px] bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none"
                        placeholder="e.g. 70s rock, electric guitar"
                      />
                    </td>
                    <td className="py-2 px-2">
                      {/* FULL TRACK cell: upload button + always-visible scrubber */}
                      <div className="flex flex-col gap-1 min-w-[260px]">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac';
                              input.onchange = (e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadOnePreview(id, f);
                              };
                              input.click();
                            }}
                            disabled={uploadingIds.has(id)}
                            className={`px-2 py-1 text-xs rounded transition flex items-center gap-1 ${
                              fullUrl
                                ? 'bg-white/5 hover:bg-white/10 text-zinc-300'
                                : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200'
                            }`}
                            title={fullUrl ? 'Replace full track' : 'Upload the full MusicGPT track'}
                            data-testid={`admin-preset-upload-${id}`}
                          >
                            {uploadingIds.has(id) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Upload className="h-3 w-3" />
                            )}
                            {fullUrl ? 'Replace' : 'Upload'}
                          </button>
                          {fullUrl && (
                            <>
                              <span className="text-zinc-500 text-xs">
                                {fullDur ? `${Math.round(fullDur)}s total` : 'ready'}
                              </span>
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white"
                                title="Open full track in new tab"
                                data-testid={`admin-preset-full-url-${id}`}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </>
                          )}
                        </div>
                        {fullUrl && (
                          <audio
                            id={`preset-full-audio-${id}`}
                            src={fullUrl}
                            controls
                            preload="none"
                            className="w-full h-8"
                            style={{ maxWidth: 260 }}
                            data-testid={`admin-preset-play-full-${id}`}
                            onTimeUpdate={(e) => {
                              // Surface currentTime so admin can grab the value
                              // for the Trim start_sec without typing manually.
                              const t = e.target.currentTime;
                              if (t != null) {
                                const display = document.getElementById(`preset-current-time-${id}`);
                                if (display) display.textContent = t.toFixed(1) + 's';
                              }
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      {/* PREVIEW cell: start_sec input + Trim button + always-visible preview player */}
                      <div className="flex flex-col gap-1 min-w-[260px]">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={startDisplay}
                            onChange={(e) => updateField(id, 'preview_start_sec', e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="start"
                            className="w-16 bg-black/30 text-white text-sm px-2 py-1 rounded border border-white/10 outline-none"
                            title="Start time (seconds) — set this to the chorus entry point"
                            data-testid={`admin-preset-start-sec-${id}`}
                          />
                          <span className="text-zinc-500 text-xs">s</span>
                          <button
                            type="button"
                            onClick={() => {
                              const el = document.getElementById(`preset-full-audio-${id}`);
                              if (el) {
                                const t = Number((el.currentTime || 0).toFixed(1));
                                updateField(id, 'preview_start_sec', t);
                              }
                            }}
                            disabled={!fullUrl}
                            className="px-1.5 py-1 text-xs rounded bg-white/5 hover:bg-white/10 text-zinc-300 disabled:opacity-40"
                            title="Grab current playback time from full track as the start"
                            data-testid={`admin-preset-grab-time-${id}`}
                          >
                            ⏱ <span id={`preset-current-time-${id}`}>0.0s</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const s = startSec ?? 0;
                              trimPreview(id, s, 12);
                            }}
                            disabled={!fullUrl || trimming || startSec === null || startSec === undefined}
                            className="px-2 py-1 text-xs rounded transition flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={!fullUrl ? 'Upload a full track first' : 'Trim 12s starting at this time'}
                            data-testid={`admin-preset-trim-${id}`}
                          >
                            {trimming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Scissors className="h-3 w-3" />}
                            Trim 12s
                          </button>
                          {previewUrl && (
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-zinc-400 hover:text-white ml-1"
                              title="Open preview in new tab"
                              data-testid={`admin-preset-preview-url-${id}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                        {previewUrl && (
                          <audio
                            id={`preset-preview-audio-${id}`}
                            src={previewUrl}
                            controls
                            preload="none"
                            className="w-full h-8"
                            style={{ maxWidth: 260 }}
                            data-testid={`admin-preset-play-${id}`}
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <button
                        onClick={() => saveRow(p)}
                        disabled={!dirty || saving}
                        className={`px-2 py-1 text-xs rounded transition flex items-center gap-1 ${
                          dirty
                            ? 'bg-purple-500 hover:bg-purple-400 text-white'
                            : 'bg-white/5 text-zinc-500 cursor-not-allowed'
                        }`}
                        data-testid={`admin-preset-save-${id}`}
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
